import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RECURRENCE = [
  { v: "none", l: "Non, ponctuelle" },
  { v: "monthly", l: "Tous les mois" },
  { v: "bimonthly", l: "Tous les 2 mois" },
  { v: "quarterly", l: "Tous les 3 mois" },
  { v: "semiannual", l: "Tous les 6 mois" },
  { v: "annual", l: "Tous les ans" },
];

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export default function TransactionDialog({ open, onOpenChange, type, editing, onSaved }) {
  const [cats, setCats] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({});

  useEffect(() => {
    if (!open) return;
    api.get(`/categories?type=${type}`).then((r) => setCats(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    if (editing) {
      setF({
        label: editing.label, amount: editing.amount, category_id: editing.category_id || "",
        site_id: editing.site_id || "", transaction_date: editing.transaction_date,
        status: editing.status, payment_method: editing.payment_method || "Virement",
        notes: editing.notes || "", actual_amount: editing.actual_amount ?? "",
      });
    } else {
      setF({ label: "", amount: "", category_id: "", site_id: "", transaction_date: today(),
             status: "planned", payment_method: "Virement", notes: "", recurrence: "none" });
    }
  }, [open, type, editing]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.amount || !f.label) { toast.error("Libellé et montant requis"); return; }
    setLoading(true);
    try {
      const payload = { ...f, type };
      if (payload.site_id === "none") payload.site_id = null;
      if (payload.category_id === "none") payload.category_id = null;
      if (editing) {
        await api.put(`/transactions/${editing.id}`, payload);
        toast.success("Modifié");
      } else {
        const r = await api.post("/transactions", payload);
        toast.success(r.data.created > 1 ? `${r.data.created} échéances créées` : "Enregistré");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const isIncome = type === "income";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-md" data-testid="transaction-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {editing ? "Modifier" : isIncome ? "Nouvelle recette" : "Nouvelle dépense"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label>Libellé</Label>
            <Input data-testid="tx-label" value={f.label || ""} onChange={(e) => set("label", e.target.value)} className="mt-1.5 rounded-xl" placeholder={isIncome ? "CA Saint-Joseph" : "Loyer, prothèses…"} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Montant (€)</Label>
              <Input data-testid="tx-amount" type="number" step="0.01" value={f.amount ?? ""} onChange={(e) => set("amount", e.target.value)} className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label>{isIncome ? "Date" : "Date de décaissement"}</Label>
              <Input data-testid="tx-date" type="date" value={f.transaction_date || ""} onChange={(e) => set("transaction_date", e.target.value)} className="mt-1.5 rounded-xl" />
            </div>
          </div>
          <div>
            <Label>Catégorie</Label>
            <Select value={f.category_id || "none"} onValueChange={(v) => set("category_id", v)}>
              <SelectTrigger data-testid="tx-category" className="mt-1.5 rounded-xl"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sans catégorie</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Site</Label>
              <Select value={f.site_id || "none"} onValueChange={(v) => set("site_id", v)}>
                <SelectTrigger data-testid="tx-site" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Général</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={f.status || "planned"} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="tx-status" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Prévu</SelectItem>
                  <SelectItem value="completed">{isIncome ? "Encaissé" : "Payé"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {f.status === "completed" && (
            <div>
              <Label>Montant réel (€)</Label>
              <Input data-testid="tx-actual" type="number" step="0.01" value={f.actual_amount ?? ""} onChange={(e) => set("actual_amount", e.target.value)} className="mt-1.5 rounded-xl" placeholder="Si différent du prévu" />
            </div>
          )}
          {!editing && (
            <div>
              <Label>Cette opération se répète-t-elle ?</Label>
              <Select value={f.recurrence || "none"} onValueChange={(v) => set("recurrence", v)}>
                <SelectTrigger data-testid="tx-recurrence" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RECURRENCE.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Notes</Label>
            <Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-1.5 rounded-xl" rows={2} />
          </div>
          <Button data-testid="tx-submit" onClick={submit} disabled={loading} className="w-full rounded-full bg-[#065F46] py-6 font-semibold hover:bg-[#047857]">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : editing ? "Enregistrer" : "Ajouter"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
